import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { coachService } from '../services/coachService';
import { TrendingUp, TrendingDown, Minus, Target, HelpCircle } from 'lucide-react';

const CoachProgressCard = ({ t }) => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [summaryData, planData] = await Promise.all([
        coachService.getSummary('7d'),
        coachService.getPlan()
      ]);
      
      setSummary(summaryData);
      setPlan(planData);
    } catch (error) {
      console.error('Error loading coach progress:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!plan) {
    return null;
  }

  const score = plan.score || 0;
  const progress = plan.progress || {};
  const goals = plan.goals || {};
  
  const sessionsPercent = goals.sessions_target > 0
    ? Math.round((progress.sessions_done / goals.sessions_target) * 100)
    : 0;

  // Determine trend (simplified)
  const trend = score >= 70 ? 'up' : score >= 40 ? 'stable' : 'down';

  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUp size={20} className="text-green-500" />;
    if (trend === 'down') return <TrendingDown size={20} className="text-orange-500" />;
    return <Minus size={20} className="text-gray-500" />;
  };

  const getTrendLabel = () => {
    if (trend === 'up') return t?.trendUp || 'En progression';
    if (trend === 'down') return t?.trendDown || 'À améliorer';
    return t?.trendStable || 'Stable';
  };

  return (
    <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer text-white" onClick={(e) => {
      // Don't navigate if clicking on the help icon
      if (e.target.closest('.help-icon')) return;
      navigate('/coach');
    }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Target className="text-blue-400" size={20} />
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">
              {t?.weeklyCoachScore || 'Weekly Coach Score'}
            </h3>
            <div className="group relative help-icon inline-block">
              <HelpCircle className="w-5 h-5 text-white opacity-80 cursor-help hover:opacity-100 transition-opacity" />
              <div className="invisible group-hover:visible absolute left-0 bottom-full mb-3 w-96 p-4 text-gray-800 text-sm rounded-lg shadow-2xl z-50 leading-relaxed pointer-events-none" style={{backgroundColor: '#ffffff'}}>
                {t?.weeklyCoachScoreTooltip || 
                  'This score reflects only your activity for the current week. It considers: number of completed sessions, therapy minutes, progress toward your weekly goal. It measures your short-term engagement, not your long-term improvement.'}
              </div>
            </div>
          </div>
        </div>
        {getTrendIcon()}
      </div>

      {/* Score */}
      <div className="mb-4">
        <div className="flex items-baseline gap-2">
          <span className="text-4xl font-bold">{score}</span>
          <span className="text-xl opacity-60">/100</span>
        </div>
        <p className="text-sm opacity-80">{getTrendLabel()}</p>
        <p className="text-xs opacity-70 mt-1">
          {t?.weeklyCoachScoreSubtitle || 'Your activity and engagement this week.'}
        </p>
      </div>

      {/* Weekly progress */}
      <div className="mb-3">
        <div className="flex justify-between text-sm opacity-80 mb-1">
          <span>{t?.weeklyGoal || 'Objectif hebdo'}</span>
          <span>{progress.sessions_done || 0} / {goals.sessions_target || 0}</span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-2">
          <div
            className="bg-blue-400 h-2 rounded-full transition-all"
            style={{ width: `${Math.min(sessionsPercent, 100)}%` }}
          />
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-3 gap-3 pt-3 border-t border-gray-700">
        <div className="text-center">
          <div className="text-lg font-bold">{progress.sessions_done || 0}</div>
          <div className="text-xs opacity-70">{t?.sessions || 'Séances'}</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold">{progress.mins_done || 0}</div>
          <div className="text-xs opacity-70">{t?.minutes || 'Minutes'}</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold">{progress.streak_days || 0}</div>
          <div className="text-xs opacity-70">{t?.streak || 'Jours'}</div>
        </div>
      </div>

      {/* View plan link */}
      <div className="mt-4 pt-3 border-t border-gray-700">
        <button className="text-sm text-blue-400 hover:text-blue-300 font-medium">
          {t?.viewFullPlan || 'Voir le plan complet'} →
        </button>
      </div>
    </div>
  );
};

export default CoachProgressCard;
